// src/app/core/shared/analysis.store.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

export type YearKey = string;
export type MetricValue =
    | 'controlled'
    | 'treated_not_controlled'
    | 'diagnosed'
    | 'undiagnosed'
    | 'prevalence'
    | 'screened'
    | 'aware_of_screening'
    | 'cvd_events'
    | 'cvd_patients'
    | 'cvd_deaths';
export type GroupValue = 'total' | 'male' | 'female' | 'average';

// ADD: Cost metrics type
export type CmShape = Record<string, Record<YearKey, number>>;

export type SeriesPoint = { x: number; y: number };
export type RunRows = Array<any>;
export type HtnMap = Record<string, Record<YearKey, number>>;

export interface AnalysisRun {
    id: string;
    title: string;
    color: string;
    rows: RunRows;
    htn?: HtnMap | null;
    // ADD: Persist normalized cost metrics for this run
    costCm?: CmShape | null;
}

export interface CombinedSeriesItem {
    runId: string;
    label: string;
    color?: string;
    points: SeriesPoint[];
}


const SELECTED_RUN_STORAGE_KEY = 'selected_run_id';

@Injectable({ providedIn: 'root' })
export class AnalysisStore {
    private readonly LS_RUNS_KEY = 'analysis_runs';
    constructor() {
        this.loadFromLocalStorage();
        const saved = localStorage.getItem(SELECTED_RUN_STORAGE_KEY);
        if (saved) {
            this.selectedRunIdSubject.next(saved);
        }
        this.selectedRunId$.subscribe(id => {
            try {
                if (id) localStorage.setItem(SELECTED_RUN_STORAGE_KEY, id);
                else localStorage.removeItem(SELECTED_RUN_STORAGE_KEY);
            } catch { }
        });
    }

    private readonly runsSubject = new BehaviorSubject<AnalysisRun[]>([]);
    readonly runsList$ = this.runsSubject.asObservable();

    private readonly selectedRunIdSubject = new BehaviorSubject<string | null>(null);
    readonly selectedRunId$ = this.selectedRunIdSubject.asObservable();

    readonly metric$ = new BehaviorSubject<MetricValue>('controlled');
    readonly group$ = new BehaviorSubject<GroupValue>('total');

    addRun(id: string, title: string, color: string, rows: RunRows, htn?: HtnMap | null): void {
        const run: AnalysisRun = { id, title, color, rows: Array.isArray(rows) ? rows : [], htn: htn ?? null, costCm: null };
        const next = [...this.runsSubject.value, run];
        this.runsSubject.next(next);
        if (!this.selectedRunIdSubject.value) {
            this.selectedRunIdSubject.next(id);
        }
        this.saveToLocalStorage();
    }

    renameRun(id: string, newTitle: string): void {
        const next = this.runsSubject.value.map(r => (r.id === id ? { ...r, title: newTitle } : r));
        this.runsSubject.next(next);
        this.saveToLocalStorage();
    }

    removeRun(id: string): void {
        const next = this.runsSubject.value.filter(r => r.id !== id);
        this.runsSubject.next(next);
        if (this.selectedRunIdSubject.value === id) {
            const fallback = next.length ? next[next.length - 1].id : null;
            this.selectedRunIdSubject.next(fallback);
        }
        this.saveToLocalStorage();
    }

    selectRun(id: string): void {
        this.selectedRunIdSubject.next(id);
        // optional: persist immediately
        this.saveToLocalStorage();
    }
    clearAll(): void {
        // Wipe in-memory state
        this.runsSubject.next([]);
        this.selectedRunIdSubject.next(null);

        // Wipe persisted keys
        try {
            localStorage.removeItem(this.LS_RUNS_KEY);
            localStorage.removeItem(SELECTED_RUN_STORAGE_KEY);
        } catch { }

        // If you want to notify subscribers immediately
        // (runsList$ and selectedRunId$ already emitted by next() above)
    }

    reloadFromLocalStorage(): void {
        try {
            const raw = localStorage.getItem(this.LS_RUNS_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            this.runsSubject.next(Array.isArray(parsed) ? parsed : []);
        } catch {
            this.runsSubject.next([]);
        }

        const savedSel = localStorage.getItem(SELECTED_RUN_STORAGE_KEY);
        this.selectedRunIdSubject.next(savedSel || null);
    }

    hasRuns(): boolean {
        return this.runsSubject.value.length > 0;
    }

    getRunsSnapshot(): AnalysisRun[] {
        return this.runsSubject.value;
    }

    getSelectedRunId(): string | null {
        return this.selectedRunIdSubject.value;
    }

    getRunById(id: string): AnalysisRun | undefined {
        return this.runsSubject.value.find(r => r.id === id);
    }

    getSelectedRun(): AnalysisRun | undefined {
        const sel = this.selectedRunIdSubject.value;
        return sel ? this.getRunById(sel) : undefined;
    }

    // KPI chart builder (unchanged)
    private valueFor(row: any, metric: MetricValue, group: GroupValue): number {
        switch (metric) {
            case 'controlled': {
                if (group === 'total') {
                    const total = row.controlled_total;
                    return total !== undefined && total !== null
                        ? Number(total)
                        : Number(row.controlled_male ?? 0) + Number(row.controlled_female ?? 0);
                }
                if (group === 'male') return Number(row.controlled_male ?? 0);
                if (group === 'female') return Number(row.controlled_female ?? 0);
                const male = Number(row.controlled_male ?? 0);
                const female = Number(row.controlled_female ?? 0);
                return (male + female) / 2;
            }
            case 'treated_not_controlled': {
                const male = Number(row.treated_not_controlled_male ?? 0);
                const female = Number(row.treated_not_controlled_female ?? 0);
                if (group === 'total') return male + female;
                if (group === 'male') return male;
                if (group === 'female') return female;
                return (male + female) / 2;
            }
            case 'diagnosed': {
                const total = Number(row.diagnosed_total ?? 0);
                if (group === 'total') return total;
                if (group === 'male') return Number(row.diagnosed_male ?? 0);
                if (group === 'female') return Number(row.diagnosed_female ?? 0);
                const male = Number(row.diagnosed_male ?? 0);
                const female = Number(row.diagnosed_female ?? 0);
                return (male + female) / 2;
            }
            case 'undiagnosed': {
                const total = Number(row.undiagnosed_total ?? 0);
                if (group === 'total') return total;
                if (group === 'male') return Number(row.undiagnosed_male ?? 0);
                if (group === 'female') return Number(row.undiagnosed_female ?? 0);
                const male = Number(row.undiagnosed_male ?? 0);
                const female = Number(row.undiagnosed_female ?? 0);
                return (male + female) / 2;
            }
            case 'prevalence': {
                const total = Number(row.prevalence_total ?? 0);
                if (group === 'total' || group === 'average') return total;
                if (group === 'male') return Number(row.cvd_patients_male ?? 0);
                if (group === 'female') return Number(row.cvd_patients_female ?? 0);
                return total;
            }
            case 'screened': {
                if (group === 'total') {
                    return Number(row.screened_practice_male ?? 0) + Number(row.screened_practice_female ?? 0);
                }
                if (group === 'male') return Number(row.screened_practice_male ?? 0);
                if (group === 'female') return Number(row.screened_practice_female ?? 0);
                const male = Number(row.screened_practice_male ?? 0);
                const female = Number(row.screened_practice_female ?? 0);
                return (male + female) / 2;
            }
            case 'aware_of_screening': {
                if (group === 'total') {
                    return Number(row.aware_of_screening_knowledge_male ?? 0) +
                        Number(row.aware_of_screening_knowledge_female ?? 0);
                }
                if (group === 'male') return Number(row.aware_of_screening_knowledge_male ?? 0);
                if (group === 'female') return Number(row.aware_of_screening_knowledge_female ?? 0);
                const male = Number(row.aware_of_screening_knowledge_male ?? 0);
                const female = Number(row.aware_of_screening_knowledge_female ?? 0);
                return (male + female) / 2;
            }
            case 'cvd_events': {
                if (group === 'total' || group === 'average') return Number(row.cvd_events_total ?? 0);
                if (group === 'male') return Number(row.cvd_events_male ?? 0);
                if (group === 'female') return Number(row.cvd_events_female ?? 0);
                return Number(row.cvd_events_total ?? 0);
            }
            case 'cvd_patients': {
                const total = this.pick(row, ['cvd_patients_total', 'cvd_patients', 'CVD_Patients_total', 'CVD_Patients']);
                const male = this.pick(row, ['cvd_patients_male', 'CVD_Patients_Male']);
                const female = this.pick(row, ['cvd_patients_female', 'CVD_Patients_Female']);

                if (group === 'total') {
                    // Fallback to male + female when a precomputed total is absent
                    return total || (male + female);
                }
                if (group === 'male') {
                    return male;
                }
                if (group === 'female') {
                    return female;
                }
                // Average: prefer (male + female) / 2; if both missing, fall back to total
                const sumMF = male + female;
                return sumMF > 0 ? sumMF / 2 : total;
            }
            case 'cvd_deaths': {
                if (group === 'total' || group === 'average') return Number(row.cvd_death_total ?? 0);
                if (group === 'male') return Number(row.cvd_death_male ?? 0);
                if (group === 'female') return Number(row.cvd_death_female ?? 0);
                return Number(row.cvd_death_total ?? 0);
            }
            default:
                return 0;
        }
    }

    readonly combinedSeries$: Observable<CombinedSeriesItem[]> = combineLatest([
        this.runsList$,
        this.metric$,
        this.group$
    ]).pipe(
        map(([runs, metric, group]) => {
            const byRun = (runs || []).map(run => {
                const rows = Array.isArray(run.rows) ? run.rows : [];
                const points: SeriesPoint[] = rows
                    .map((row: any) => ({ x: Number(row.year), y: this.valueFor(row, metric, group) }))
                    .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y));
                return { runId: run.id, label: run.title, color: run.color || '#ef4444', points };
            });
            return byRun.filter(s => s.points.length > 0);
        })
    );

    // ----------------- Cost metrics persistence (added, non-breaking) -----------------

    // Set/replace cost metrics CM for a given run
    setRunCostCm(runId: string, costCm: CmShape | null): void {
        const next = this.runsSubject.value.map(r => (r.id === runId ? { ...r, costCm } : r));
        this.runsSubject.next(next);
        this.saveToLocalStorage();
    }

    // Convenience getter
    getRunCostCm(runId: string): CmShape | null {
        return this.getRunById(runId)?.costCm ?? null;
    }

    // Upsert normalized cost metrics CM from any simulation API response
    upsertRunCostCmFromApiResponse(resp: any): void {
        const runId =
            resp?.run_id ??
            resp?.id ??
            resp?.payload?.[0]?.run_id ??
            this.getSelectedRunId();

        if (!runId) {
            console.warn('[AnalysisStore] upsertRunCostCm: no run id found in response');
            return;
        }

        // Try explicit paths first
        const rawCm =
            resp?.costCm ??
            resp?.payload?.[0]?.response?.costCm ??
            resp?.payload?.[0]?.response?.cm ??
            resp?.payload?.[0]?.request?.cm ??
            resp?.cm ??
            null;

        // If not, deep search for a cost-like CM
        const cmCandidate = rawCm ?? this.findBestCostCmDeep(resp)?.cm ?? null;

        if (!cmCandidate) {
            console.warn('[AnalysisStore] upsertRunCostCm: no cost CM found for run', runId);
            return;
        }

        const normalized = this.normalizeCostCm(cmCandidate);
        this.setRunCostCm(String(runId), normalized);
        // console.log('[AnalysisStore] costCm keys', Object.keys(normalized).slice(0, 20));
    }

    // Normalize/alias cost Cm keys to the canonical keys you listed
    private normalizeCostCm(cmRaw: Record<string, Record<YearKey, number>>): CmShape {
        const out: CmShape = {};
        const ranges: Array<'three' | 'five' | 'ten' | 'fifteen'> = ['three', 'five', 'ten', 'fifteen'];

        const R = (r: 'three' | 'five' | 'ten' | 'fifteen') => (r === 'fifteen' ? ['fifteen', 'fifeen'] : [r]);

        const put = (canonical: string, variants: string[]) => {
            const series = this.findSeries(cmRaw, variants);
            if (series) out[canonical] = series;
        };

        // Net Benefits: Intervention + Health System + QALY + Monetized + valued_each_QALY
        for (const r of ranges) {
            const rr = R(r);

            // Intervention (Net Benefits)
            put(`cummulative_intervention_cost_current_${r}`, [
                ...rr.map(x => `cummulative_intervention_cost_current_${x}`),
                ...rr.map(x => `cumulative_intervention_cost_current_${x}`)
            ]);

            // Health System (Net Benefits)
            put(`cummulative_health_sector_cost_current_${r}`, [
                ...rr.map(x => `cummulative_health_sector_cost_current_${x}`),
                ...rr.map(x => `cumulative_health_sector_cost_current_${x}`)
            ]);

            // QALY (Base/Current)
            put(`cummulative_QALY_current_${r}`, [
                ...rr.map(x => `cummulative_QALY_current_${x}`),
                ...rr.map(x => `cumulative_QALY_current_${x}`),
                ...rr.map(x => `cummulative_QALY_${x}`),
                ...rr.map(x => `cumulative_QALY_${x}`)
            ]);

            // Monetised QALY gains (Base/Current)
            put(`cummulative_monetized_QALY_gains_current_${r}`, [
                ...rr.map(x => `cummulative_monetized_QALY_gains_current_${x}`),
                ...rr.map(x => `cumulative_monetized_QALY_gains_current_${x}`),
                ...rr.map(x => `cummulative_monetized_QALY_gains_${x}`),
                ...rr.map(x => `cumulative_monetized_QALY_gains_${x}`)
            ]);

            // Scenario Comparison: Monetized QALYs
            put(`scenario_cummulative_monetized_QALY_gains_current_${r}`, [
                ...rr.map(x => `cummulative_monetized_QALY_gains_current_${x}`),
                ...rr.map(x => `cumulative_monetized_QALY_gains_current_${x}`)
            ]);

            // Scenario Comparison: Health Sector Cost (intervention typo ‘invention’)
            put(`cummulative_invention_health_sector_cost_current_${r}`, [
                ...rr.map(x => `cummulative_invention_health_sector_cost_current_${x}`),
                ...rr.map(x => `cumulative_invention_health_sector_cost_current_${x}`),
                ...rr.map(x => `cummulative_intervention_health_sector_cost_current_${x}`),
                ...rr.map(x => `cumulative_intervention_health_sector_cost_current_${x}`)
            ]);

            // net_benefit_health_sector_current
            put(`net_benefit_health_sector_current_${r}`, [
                ...rr.map(x => `net_benefit_health_sector_current_${x}`)
            ]);

            // Broader Societal cost
            put(`cumulative_social_cost_current_${r}`, [
                ...rr.map(x => `cumulative_social_cost_current_${x}`)
            ]);

            // Intervention Health Sector Social cost (typo/variants)
            put(`cummulative_invention_health_sector_social_cost_current_${r}`, [
                ...rr.map(x => `cummulative_invention_health_sector_social_cost_current_${x}`),
                ...rr.map(x => `cumulative_invention_health_sector_social_cost_current_${x}`),
                ...rr.map(x => `cummulative_intervention_health_sector_social_cost_current_${x}`),
                ...rr.map(x => `cumulative_intervention_health_sector_social_cost_current_${x}`)
            ]);

            // Cost Analysis: Intervention Capital
            put(`total_investmentcost_10_interventions_current_${r}`, [
                ...rr.map(x => `total_investmentcost_10_interventions_current_${x}`),
                ...rr.map(x => `total_investment_cost_10_interventions_current_${x}`),
                ...rr.map(x => `total_investmentcost_10_interventions_${x}`),
                ...rr.map(x => `total_investment_cost_10_interventions_${x}`)
            ]);

            // Cost Analysis: Intervention Running (handles accidental space)
            put(`total_running_cost_10_interventions_current_${r}`, [
                ...rr.map(x => `total_running_cost_10_interventions_current_${x}`),
                ...rr.map(x => `total_running_cost_10 _interventions_current_${x}`),
                ...rr.map(x => `total_running_cost_10_interventions_${x}`),
                ...rr.map(x => `total_running_cost_10 _interventions_${x}`)
            ]);

            // Cost Analysis: Total Intervention Cost
            put(`cummulative_intervention_cost_current_${r}`, [
                ...rr.map(x => `cummulative_intervention_cost_current_${x}`),
                ...rr.map(x => `cumulative_intervention_cost_current_${x}`),
                ...rr.map(x => `cummulative_intervention_cost_${x}`),
                ...rr.map(x => `cumulative_intervention_cost_${x}`)
            ]);

            // Health System Cost: Primary Care
            put(`cumulative_primary_care_cost_current_${r}`, [
                ...rr.map(x => `cumulative_primary_care_cost_current_${x}`),
                ...rr.map(x => `cumulative_primary_care_cost_${x}`)
            ]);

            // Health System Cost: Secondary Care (CVD events)
            put(`cumulative_CVD_event_cost_current_${r}`, [
                ...rr.map(x => `cumulative_CVD_event_cost_current_${x}`),
                ...rr.map(x => `cumulative_cvd_event_cost_current_${x}`),
                ...rr.map(x => `cumulative_CVD_event_cost_${x}`),
                ...rr.map(x => `cumulative_cvd_event_cost_${x}`)
            ]);

            // Total Health Sector Cost
            put(`cummulative_health_sector_cost_current_${r}`, [
                ...rr.map(x => `cummulative_health_sector_cost_current_${x}`),
                ...rr.map(x => `cumulative_health_sector_cost_current_${x}`),
                ...rr.map(x => `cummulative_health_sector_cost_${x}`),
                ...rr.map(x => `cumulative_health_sector_cost_${x}`)
            ]);

            // Broader Societal: Productivity patients and carers
            put(`total_productivity_loss_current_${r}`, [
                ...rr.map(x => `total_productivity_loss_current_${x}`),
                ...rr.map(x => `total_productivity_loss_${x}`)
            ]);

            // Broader Societal: Social cost (used above)
            put(`cumulative_social_cost_current_${r}`, [
                ...rr.map(x => `cumulative_social_cost_current_${x}`),
                ...rr.map(x => `cumulative_social_cost_${x}`)
            ]);

            // Total Funeral Cost
            put(`total_funeral_cost_current_${r}`, [
                ...rr.map(x => `total_funeral_cost_current_${x}`),
                ...rr.map(x => `total_funeral_cost_${x}`)
            ]);

            put(`net_benefit_health_and_social_sector_current_${r}`, [
                ...rr.map(x => `net_benefit_health_and_social_sector_current_${x}`),
                ...rr.map(x => `net_benefit_health_social_sector_current_${x}`),
                ...rr.map(x => `net_benefit_health_and_social_current_${x}`)
            ]);
        }

        // $ per QALY gained (scalar series may be under a single key with years or a plain number)
        const vq = cmRaw['valued_each_QALY'];
        if (vq && typeof vq === 'object') {
            out['valued_each_QALY'] = vq;
        }

        return out;
    }

    private findSeries(cm: Record<string, Record<YearKey, number>>, variants: string[]): Record<YearKey, number> | null {
        for (const k of variants) {
            if (cm[k]) return cm[k]!;
        }
        // fuzzy fallback: normalize and match ignoring underscores/spaces/case
        const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        const catalog = Object.keys(cm).map(k => ({ k, n: norm(k) }));
        for (const v of variants) {
            const nv = norm(v);
            const hit = catalog.find(c => c.n.includes(nv));
            if (hit) return cm[hit.k]!;
        }
        return null;
    }

    // Deep-search for cost-like CM object inside arbitrary response/run
    private findBestCostCmDeep(root: any): { cm: CmShape; path: string } | null {
        const visited = new Set<any>();
        const stack: Array<{ node: any; path: string }> = [{ node: root, path: 'root' }];
        let best: { cm: CmShape; path: string; score: number } | null = null;

        const tokens = [
            'cost',
            'investment',
            'running',
            'primary',
            'secondary',
            'health',
            'sector',
            'productivity',
            'funeral',
            'social',
            'qaly',
            'monetized',
            'intervention',
            'invention',
            'net_benefit'
        ];

        while (stack.length) {
            const { node, path } = stack.pop()!;
            if (!node || typeof node !== 'object') continue;
            if (visited.has(node)) continue;
            visited.add(node);

            const score = this.scoreCostCandidate(node, tokens);
            if (score >= 2) {
                if (!best || score > best.score) {
                    best = { cm: node as CmShape, path, score };
                }
            }

            if (Array.isArray(node)) {
                node.forEach((item, i) => stack.push({ node: item, path: `${path}[${i}]` }));
            } else {
                for (const key of Object.keys(node)) {
                    stack.push({ node: (node as any)[key], path: `${path}.${key}` });
                }
            }
        }

        return best ? { cm: best.cm, path: best.path } : null;
    }

    private scoreCostCandidate(obj: any, tokens: string[]): number {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 0;
        let score = 0;
        for (const [k, v] of Object.entries(obj)) {
            const key = String(k).toLowerCase();
            const hit = tokens.some(t => key.includes(t));
            if (hit && this.isYearSeries(v)) score++;
        }
        return score;
    }

    private isYearSeries(obj: any): boolean {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
        const keys = Object.keys(obj);
        if (keys.length < 3) return false;
        let count = 0;
        for (const y of keys) if (/^\d{4}$/.test(y) && typeof (obj as any)[y] === 'number') count++;
        return count >= Math.max(3, Math.floor(keys.length * 0.5));
    }

    // Persistence
    private saveToLocalStorage(): void {
        try {
            localStorage.setItem(this.LS_RUNS_KEY, JSON.stringify(this.runsSubject.value));
            localStorage.setItem(SELECTED_RUN_STORAGE_KEY, this.selectedRunIdSubject.value ?? '');
        } catch { }
    }
    private loadFromLocalStorage(): void {
        try {
            const raw = localStorage.getItem(this.LS_RUNS_KEY);
            if (!raw) {
                this.runsSubject.next([]);
                return;
            }
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                // Parsed objects already contain costCm if you saved it earlier
                this.runsSubject.next(parsed as AnalysisRun[]);
            } else {
                this.runsSubject.next([]);
            }
        } catch {
            this.runsSubject.next([]);
        }
    }

    // Read the first available numeric value from candidate keys
    private pick(row: any, keys: string[]): number {
        if (!row) return 0;
        for (const k of keys) {
            const v = (row as any)[k];
            const n = Number(v);
            if (v !== undefined && v !== null && Number.isFinite(n)) return n;
        }
        return 0;
    }
}